import { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { authService } from '../../services/authService';
import LoaderOverlay from '../../components/LoaderOverlay';
import Swal from 'sweetalert2';
import { SECURE_AUTH_TOAST as T } from '../../utils/secureAuthToastMessages';

const TwoFactorAuth = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { verify2FA, isVerifying2FA, isAuthenticated, user } = useAuth();

    const { staffId, ownerId, method } = location.state || {};
    // The ROUTE decides the method — /loginauth/3 is the Google screen, /2 the
    // Telegram one. Reading it from the path rather than location.state means a
    // refresh (or a pasted URL) still renders the right prompt.
    // Google codes are generated by the Authenticator app: nothing was sent, so
    // there is nothing to re-send and no "telegram bot" to name.
    // /3 also serves 'android' (our Secure Auth Verification App) — same
    // rules, only the heading names the other app.
    const isGoogle = location.pathname.endsWith('/loginauth/3') || method === 'google' || method === 'android';

    const [otp, setOtp] = useState(new Array(6).fill(""));

    // Same toast as Login.jsx — top-right, success icon.
    const Toast = Swal.mixin({
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: false,
        didOpen: (toast) => {
            toast.onmouseenter = Swal.stopTimer;
            toast.onmouseleave = Swal.resumeTimer;
        }
    });
    const inputRefs = useRef([]);

    // A bad code shows no toast — it just clears the boxes. A good one toasts
    // "Success" and navigates away.

    useEffect(() => {
        if (!staffId && !ownerId) {
            navigate('/admin');
        }
    }, [staffId, ownerId, navigate]);

    /**
     * A REFRESH of this screen must request a new code.
     *
     * The OTP is pushed by the login call, so before this a reload left the
     * operator staring at six empty boxes with no way to fill them — the code
     * from before the reload was still valid, but nobody who reloads has it.
     *
     * Login sets a one-shot `admin2FAFresh` flag immediately before navigating
     * here: its presence means the OTP was JUST sent, so we only consume it. On
     * a reload the flag is gone (location.state is NOT the signal — it survives
     * a reload via history state), and that absence triggers the resend.
     *
     * The ref guards React 18 StrictMode's double-invoke, which would otherwise
     * send two codes and leave the first one dead.
     */
    const resendGuard = useRef(false);
    useEffect(() => {
        if (resendGuard.current) return;
        if (!staffId && !ownerId) return;
        resendGuard.current = true;

        const fresh = sessionStorage.getItem('admin2FAFresh');
        if (fresh) {
            sessionStorage.removeItem('admin2FAFresh');
            return;
        }

        // Google: nothing is ever pushed, so a refresh has no code to re-request.
        if (isGoogle) return;

        // Silent by design — this screen shows no messages. A failure just
        // leaves the boxes empty; Resend Code sends the operator back to login.
        (ownerId ? authService.resendOwner2FA(ownerId) : authService.resendStaff2FA(staffId))
            .catch(() => {});
    }, [staffId, ownerId, isGoogle]);

    useEffect(() => {
        if (isAuthenticated) {
            if (sessionStorage.getItem('needPasswordChange') === 'true') {
                navigate('/admin/change-password', { replace: true })
            } else if (user) {
                navigate('/admin/home', { replace: true })
            }
        }
    }, [isAuthenticated, user, navigate]);

    const handleVerify = async (enteredOtp) => {
        try {
            // On success the isAuthenticated effect above navigates away; the
            // SweetAlert toast lives on document.body, so it survives that.
            const data = await verify2FA({ otp: enteredOtp, staffId, ownerId });
            if (data?.success && data?.token) Toast.fire({ icon: 'success', text: T.LOGIN_VERIFIED });
        } catch {
            // Wrong or expired code: clear the boxes and put the cursor back on
            // the first one. No message.
            setOtp(new Array(6).fill(""));
            inputRefs.current[0]?.focus();
        }
    };

    // Each box lives in its own wrapper div (reference markup), so the old
    // `element.nextSibling.focus()` had nothing to move to — advance by ref.
    const handleChange = (element, index) => {
        if (isNaN(element.value)) return false;

        const newOtp = otp.map((d, i) => (i === index ? element.value : d));
        setOtp(newOtp);

        if (element.value !== "" && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }

        if (newOtp.every(val => val !== "")) {
            handleVerify(newOtp.join(""));
        }
    };

    const handleKeyDown = (e, index) => {
        if (e.key === "Backspace" && !otp[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData("text").slice(0, 6);
        if (!/^\d+$/.test(pastedData)) return;

        const newOtp = [...otp];
        pastedData.split("").forEach((char, i) => {
            if (i < 6) newOtp[i] = char;
        });
        setOtp(newOtp);

        const nextIndex = Math.min(pastedData.length, 5);
        inputRefs.current[nextIndex]?.focus();

        if (newOtp.every(val => val !== "")) {
            handleVerify(newOtp.join(""));
        }
    };

    // Push a fresh code to the linked Telegram chat. Silent — this screen shows
    // no messages — so clearing the boxes is the only signal that something
    // happened, and it is also correct: the code they were typing is now dead.
    const handleResend = (e) => {
        e.preventDefault();
        setOtp(new Array(6).fill(""));
        inputRefs.current[0]?.focus();
        (ownerId ? authService.resendOwner2FA(ownerId) : authService.resendStaff2FA(staffId))
            .catch(() => {});
    };

    return (
        // Reference DOM: `.login-auth` (theme's full-height centring wrapper)
        // > `.loginInner1.authentication` > logo + `.featured-box-login`. The
        // otp boxes take their look from the `.login-auth input` rules
        // (style.css:8139), not from `.otp-input`, and `.login-auth form > div`
        // centres the row — hence no inline justify-content here.
        <div className={`login-auth${isVerifying2FA ? ' loader-section-parent' : ''}`}>
            {isVerifying2FA && <LoaderOverlay />}
            <div className="loginInner1 authentication">
                <div className="log-logo m-b-20 text-center">
                    {/* Local asset, not the reference's CDN copy */}
                    <img src="/admin/assets/logo.png" alt="logo" className="logo-login" />
                </div>
                <div className="featured-box-login featured-box-secundary default">
                    <h3 className="text-center">
                        {method === 'android'
                            ? 'Security Code Verification Using Secure Auth Verification App'
                            : isGoogle
                                ? 'Security Code Verification Using Google Authenticator App'
                                : 'Security Code Verification Using Telegram App'}
                    </h3>
                    {/* Telegram only — a Google account has no code to resend. */}
                    {!isGoogle && (
                        <div className="mt-3 text-center">
                            Enter 6-digit code from your telegram bot{' '}
                            <span>
                                <a href="#" onClick={handleResend}>Resend Code</a>
                            </span>
                        </div>
                    )}
                    {/* Verification fires on the 6th digit — no submit button.
                        The form stays so Enter still works. */}
                    <form
                        role="form"
                        autoComplete="off"
                        method="post"
                        className="mt-3"
                        onSubmit={(e) => { e.preventDefault(); handleVerify(otp.join("")); }}
                    >
                        <div style={{ display: 'flex', flexDirection: 'row' }}>
                            <div style={{ display: 'flex' }} onPaste={handlePaste}>
                                {otp.map((data, index) => (
                                    <div key={index} style={{ display: 'flex', alignItems: 'center' }}>
                                        <input
                                            min="0"
                                            max="9"
                                            maxLength={1}
                                            pattern="[0-9]"
                                            type="tel"
                                            className="otp-input"
                                            inputMode="numeric"
                                            autoComplete="off"
                                            value={data}
                                            ref={(el) => { inputRefs.current[index] = el }}
                                            onChange={(e) => handleChange(e.target, index)}
                                            onKeyDown={(e) => handleKeyDown(e, index)}
                                            onFocus={(e) => e.target.select()}
                                            disabled={isVerifying2FA}
                                            autoFocus={index === 0}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default TwoFactorAuth;
